/**
 * Which version of the entry page (/welcome) a visitor sees, and which tab of the account panel
 * is open. Every way into the app ends here: ads, search, the website, /landing/signup and
 * /landing/signin (both redirect), logging out, an expired session, a deep link without a session.
 *
 * Rules, first match wins:
 * 1. Login: `auth=login`, `session_expired`, or a `redirectTo` that is not the sign-up page
 *    (a deep link or a logout bounce: the visitor already has an account).
 * 2. The ad (`utm_content`): c (35 Stunden), d (KI-optimiert), e (ohne Copy-Paste), h (Headhunter).
 * 3. A Meta click without a known ad (fbclid, utm_source facebook/instagram/meta, or a Facebook
 *    or Instagram referrer): meta.
 * 4. A paid Google click (gclid/gbraid/wbraid or utm_source google with a paid medium): suche.
 * 5. Everything else: direkt.
 *
 * The backend knows the same entries (PUBLIC_CHAT_ENTRIES) and tells the agent what the page showed.
 */
import type { Attribution } from 'features/engagement/signup/attribution';

export const CHAT_ENTRIES = ['direkt', 'c', 'd', 'e', 'h', 'meta', 'suche', 'login'] as const;
export type ChatEntry = (typeof CHAT_ENTRIES)[number];
export type AuthTab = 'signup' | 'login';

const SIGNUP_PATH = /^(\/dashboard)?\/(register|landing\/signup)(\/|\?|$)/;
const META_SOURCE = /^(facebook|fb|instagram|ig|meta)$/i;
const META_HOST = /(^|\.)(facebook|instagram|fb|messenger)\.(com|me)$/i;
const PAID_MEDIUM = /^(cpc|ppc|paid|paid[-_]?search|sea|ads?)$/i;

const AD_VARIANTS: Array<[RegExp, ChatEntry]> = [
  [/35[-_ ]?(stunden|h)/, 'c'],
  [/ki[-_ ]?optim/, 'd'],
  [/copy[-_ ]?paste/, 'e'],
  [/headhunter/, 'h'],
];

/** The ad variant named in utm_content: "c", "ad-c", "ad_c", "pai-c-sq", "35-stunden" … */
export function adVariantOf(content: string | undefined): ChatEntry | undefined {
  if (!content) return undefined;
  const value = content.toLowerCase();
  for (const [pattern, entry] of AD_VARIANTS) if (pattern.test(value)) return entry;
  const tokens = value.split(/[^a-z0-9]+/).filter(Boolean);
  for (const letter of ['c', 'd', 'e', 'h'] as const) {
    if (tokens.includes(letter) || tokens.includes(`ad${letter}`)) return letter;
  }
  return undefined;
}

/** A search term fit to be shown on the page (letters, digits, spaces, hyphens; 3–40 characters). */
export function displayableTerm(term: string | undefined): string | undefined {
  const value = term?.trim().replace(/\s+/g, ' ');
  return value && /^[\p{L}\p{N} \-+&.]{3,40}$/u.test(value) ? value : undefined;
}

export function resolveEntry(search: string, attribution?: Attribution): { entry: ChatEntry; tab: AuthTab } {
  const params = new URLSearchParams(search);
  const redirectTo = params.get('redirectTo');
  const wantsLogin =
    params.get('auth') === 'login' ||
    params.get('session_expired') === 'true' ||
    (!!redirectTo && !SIGNUP_PATH.test(redirectTo) && params.get('auth') !== 'signup');
  if (wantsLogin) return { entry: 'login', tab: 'login' };

  const a = attribution ?? {};
  const fromAd = adVariantOf(a.utm_content);
  if (fromAd) return { entry: fromAd, tab: 'signup' };
  if (a.fbclid || META_SOURCE.test(a.utm_source ?? '') || META_HOST.test(a.ref ?? '')) return { entry: 'meta', tab: 'signup' };
  if (a.gclid || a.gbraid || a.wbraid || (/^google$/i.test(a.utm_source ?? '') && PAID_MEDIUM.test(a.utm_medium ?? ''))) {
    return { entry: 'suche', tab: 'signup' };
  }
  return { entry: 'direkt', tab: 'signup' };
}
